import moment from "moment";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { CalendarLengthButton, CalendarLengthButtonGroup, ScheduleHeaderContainer, ScheduleHeaderButtons, ScheduleTitle } from "./styles";
import { AppIconButton } from "@components";
import { ArrowLeftIcon, ArrowRightIcon } from "@assets";
import "moment/locale/ja";
import { selectAuth, selectPreloadData, useAppSelector, useAppDispatch, fetchReservations, selectReservations, selectReservationsLoading, fetchRooms, selectRooms, fetchTypes, selectTypes, updateReservation, selectLists, createList, fetchLists, deleteList, updateList } from "@redux";
import { Dialog, DialogContent } from "@mui/material";
import { useHistory } from "react-router-dom";
import { appRouteEnums } from "@enums";
import { CalendarGroupingEnum, CalendarSpanEnum, ReservationsParticipant } from "src/types";
import { Box, Button, Grid, IconButton } from '@mui/material';
import { ScheduleCalendar } from "./ScheduleCalendar";
import { AppCustomeSelect } from '../Common';
import scStyled from 'styled-components';
import { useDeviceDetection, useDynamicPadding  } from "./FrontEndHelpers";
import { ParticipantsSelector } from "./ParticipantsSelector";
import { PassChangeButton } from '../Profile';
import { CircularProgress } from "@mui/material";
import { useSortedGroupList, useSortedUserList } from "src/Utils/Hooks/useSorter";
// import { weeklyScheduleApi } from "src/Api/weekly-schedule";
import { createScheduleEvents } from "./createScheduleEvents";
import { holidayApi } from "src/Api/holiday";
import { useUserSelectionHistories } from "src/Utils/Hooks/useUserSelectionHistories";
import { useGroupSelectionHistories } from "src/Utils/Hooks/useGroupSelectionHistories";

let firstUpdate = true;

export const ScheduleView = ({ contentDisplayed = "reservations" }: { contentDisplayed?: "reservations" | "facilities" }) => {
  const reservationsDisplayed = contentDisplayed === "reservations";
  const facilitiesDisplayed = contentDisplayed === "facilities";

  //ページに応じてholidaysを取得する
  const [scheduleHolidays, setScheduleHolidays] = useState<string[]>([]);
  const [facilitiesHolidays, setFacilitiesHolidays] = useState<string[]>([]);

  //ユーザーとグループの履歴保存付きリスト用
  const [userOptions, setUserOptions] = useState<{value: string, label: string, type: string}[]>([]);
  const [groupOptions, setGroupOptions] = useState<{value: string, label: string, type: string}[]>([]);
  const [hasAutoSelected, setHasAutoSelected] = React.useState<boolean>(false);

  const dispatch = useAppDispatch();
  const history = useHistory();

  // Get user ID from auth state
  const user = useAppSelector(selectAuth);
  const userId = user.userInfo?.id;
  
  // Get holidays from preload data
  const preloadData = useAppSelector(selectPreloadData);
  // const holidays = preloadData.holidays;

  //holidaysをAdminの設定に応じて取得する値を変更
  const months = useMemo(() => {
  const list: { year: number; month: number }[] = [];
  const base = moment();
  for (let offset = -3; offset <= 3; offset++) {
    const target = base.clone().add(offset, "months");
    list.push({ year: target.year(), month: target.month() + 1 });
  }
  return list;
}, []);

  const holidays = facilitiesDisplayed ? facilitiesHolidays : scheduleHolidays;

  useEffect(() => {
    if (contentDisplayed === "facilities") {
    fetchAllHolidays("facilities");
  } else if (contentDisplayed === "reservations") {
    fetchAllHolidays("schedule");
  }
  }, [contentDisplayed]);

  // console.log("Holidays", holidays);

  const fetchAllHolidays = async (pageKey: "schedule" | "facilities") => {
    try {
      const results = await Promise.all(
        months.map(({ year, month }) =>
          holidayApi.getMonthHolidays({ year, month, page_key: pageKey })
        )
      );
      const holidayDates = results.flatMap(res =>
        res.data.data.date_holiday.map((d) => d.date)
      );
      const uniqueDates = Array.from(new Set(holidayDates));

      if (pageKey === "schedule") {
        setScheduleHolidays(uniqueDates);
      } else {
        setFacilitiesHolidays(uniqueDates);
      }
    } catch (error) {
      console.error("複数月の休日情報取得失敗", error);
    }
  };


  // Get all users and sort them
  const users = preloadData.users;
  const [selectedUserId, setSelectedUserId] = useState<number | null>(userId || null);

  //ユーザー情報の取得はselectionHistoriesから行うためコメントアウト
  // const sortedUsers = useSortedUserList(users, true, false, "Schedule");
  // const selectUsers = sortedUsers.map((user) => ({value: user.id, label: `${user.last_name} ${user.first_name}`}));
  // useEffect(() => {
  //   if (!selectedUserId && selectUsers.length > 0) {
  //     // If no user is selected, set the first user as selected
  //     const firstUser = selectUsers[0];
  //     if (firstUser && firstUser.value) {
  //       setSelectedUserId(Number(firstUser.value));
  //     }
  //   }
  // }, [selectUsers, selectedUserId]);

  const { list, fetchList, storeSelection } = useUserSelectionHistories(userId ?? 0);

  // Get groups and sort them
  const groups = preloadData.groups;
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const sortedGroups = useSortedGroupList(groups, false);

  const { groupList, fetchGroupList, storeGroupSelection } = useGroupSelectionHistories(userId ?? 0);

  //console.log("Sorted groups in ScheduleView:", sortedGroups);

  // Get schedule data from the Redux store
  const reservations = useAppSelector(selectReservations);
  const rooms = useAppSelector(selectRooms);
  const reservationsLoading = useAppSelector(selectReservationsLoading);
  const types = useAppSelector(selectTypes);

  // Get lists (favorites and default participants) from slice
  const lists = useAppSelector(selectLists).data.filter(list => !list.deleted_at && list.creator_id === userId);
  const [favoriteListValue, setFavoriteListValue] = useState<{value: number, label: string} | null>(null);

  useEffect(() => {
    if (!lists || lists.length === 0 || !userId || !firstUpdate || !list || !groupList) return;
    firstUpdate = false;

    const favoriteList = lists.find(list => list.type === 'favorite' && list.creator_id === userId);

    if (favoriteList && favoriteList.id && favoriteList.name) {
      const newValue = {value: favoriteList.id, label: favoriteList.name};
      // Only update if the actual values changed
      setFavoriteListValue(prev => 
        prev?.value !== newValue.value || prev?.label !== newValue.label 
          ? newValue 
          : prev
      );
    } else {
      // No favorite list found - ensure favoriteListValue is null
      console.log("No favorite list found for user, setting to null");
      setFavoriteListValue(null);
    }
  }, [lists, userId]);

  const [calendarSpan, setCalendarSDpan] = useState<CalendarSpanEnum>(reservationsDisplayed ? 'month' : 'week');
  const [calendarGrouping, setCalendarGrouping] = useState<CalendarGroupingEnum>(reservationsDisplayed ? 'individual' : 'facilities');
  const [currentDate, setCurrentDate] = useState(new Date());

  const stackedView = (calendarGrouping !== 'individual') && (calendarSpan === 'week');
  const columnsView = (calendarGrouping !== 'individual') && (calendarSpan === 'day');

  // Modal state for favorite/default users
  const [userSelectionType, setUserSelectionType] = useState<null | 'favorite' | 'default'>(null);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<ReservationsParticipant[]>([]);
  const [favoriteUserIds, setFavoriteUserIds] = useState<number[]>([]);
  const [groupUserIds, setGroupUserIds] = useState<number[]>([]);
  const [defaultParticipantUserIds, setDefaultParticipants] = useState<number[]>([]);

  const [calendarRoomIds, setCalendarRoomIds] = useState<number[]>([]);

  const calendarUserIds = [userId, ...groupUserIds];

  const groupsUsers = preloadData.groupsUsers;

  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);

  const headerDummyRef = useRef<HTMLDivElement>(null);
  const headerDummyIndividualWeeklyRef = useRef<HTMLDivElement>(null);

  // handler to be called by ScheduleCalendar when navigation is needed
  const handleNavigate = (newDate: Date, newView: CalendarSpanEnum) => {
    setCurrentDate(newDate);
    setCalendarSDpan(newView);
    if (newView === 'week') { 
        calculateCurrentWeekRow(newDate);
    }
  };

  // Keep the "isMobile" variable in sync with the window size
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Loading overlay NOT NEEDED but keeping just in case
//   const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
//   useEffect(() => {
  
//   let hideTimer: NodeJS.Timeout;

//   if (reservationsLoading) {
//     // Show overlay immediately when loading starts
//     setShowLoadingOverlay(true);
//   } else if (showLoadingOverlay) {
//     // Add a minimum display time when loading finishes
//     hideTimer = setTimeout(() => {
//       setShowLoadingOverlay(false);
//     }, 300); // 300ms delay before hiding
//   }

//   return () => {
    
//     clearTimeout(hideTimer);
//   };
// }, [reservationsLoading, showLoadingOverlay]);
  

  useEffect(() => {
      const el = headerDummyRef.current;
      if (!el) return;
  
      const isHeaderDummyActiveByCalendar = calendarGrouping !== 'individual' && calendarSpan === 'week';
  
      if (isHeaderDummyActiveByCalendar) {
     
          el.style.display = "block";
  
      } else {
        el.style.display = "none";
      }
  
    }, [calendarSpan, calendarGrouping, currentDate]);
  
  
   useEffect(() => {
    const el = headerDummyIndividualWeeklyRef.current;
    if (!el) return;
  
    const isHeaderDummyActiveByCalendar = calendarGrouping === 'individual' && calendarSpan === 'week';
  
    // Find the scrollable container and the .rbc-row-content element
    const scrollContainer = document.querySelector('#allCalendarElements') as HTMLElement | null;
    const rowContent = document.querySelector('.rbc-row-content') as HTMLElement | null;
  
    if (!isHeaderDummyActiveByCalendar || !rowContent || !scrollContainer) {
      el.style.display = "none";
      el.style.height = '0px';
      return;
    }
  
    el.style.display = "block";
  
    // Function to update visible height
    const updateHeight = () => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const rowRect = rowContent.getBoundingClientRect();
  
      // Calculate intersection
      const visibleTop = Math.max(containerRect.top, rowRect.top);
      const visibleBottom = Math.min(containerRect.bottom, rowRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
  
      el.style.height = `${visibleHeight + 26}px`;
    };
  
    // Initial update
    updateHeight();
  
    // Listen for scroll and resize
    scrollContainer.addEventListener('scroll', updateHeight);
    window.addEventListener('resize', updateHeight);
  
    // Clean up
    return () => {
      scrollContainer.removeEventListener('scroll', updateHeight);
      window.removeEventListener('resize', updateHeight);
    };
  }, [calendarSpan, calendarGrouping, currentDate, selectedUserId]);


  useEffect(() => {  
    dispatch(fetchRooms());
    dispatch(fetchTypes());
    dispatch(fetchReservations());
    //refreshReservations();
    dispatch(fetchLists());
  }, [dispatch, currentDate]);

  // We can't fetch reservations with a date range, for the following reason:
  // Imagine we're making a recurring reservation that is weekly and lasts for a couple years.
  // Without information about all reservations for the next couple years, we can't know if the reservation we're making overlaps with existing ones.
  // Adding start date might be ok for this, but even if the only conflicts could exist in the past, the client might still dislike that.
  const refreshReservations = () => {
    dispatch(fetchReservations());

//    // calculate days that appear outside current month
//    const bufferDays = 14; 
//    
//    const fetchStartDate = moment(currentDate)
//      .startOf('month')
//      .subtract(bufferDays, 'days')
//      .format('YYYY-MM-DD');
//      
//    const fetchEndDate = moment(currentDate)
//      .endOf('month')
//      .add(bufferDays, 'days')
//      .format('YYYY-MM-DD');
//    
//    console.log('Fetching reservations from', fetchStartDate, 'to', fetchEndDate);
//    console.log('Fetching reservations from', fetchStartDate, 'to', fetchEndDate);
//
//    dispatch(fetchReservations({
//      start_date: fetchStartDate,
//      end_date: fetchEndDate
//    }));
  };

  // Update the list of rooms when the rooms data changes
  useEffect(() => {
    if (rooms?.data) {
      // Get all room IDs from the rooms data
      const roomIds = rooms.data
      .filter(room => !room.deleted_at) // Filter out deleted rooms
      .map(room => room.id);
      setCalendarRoomIds(roomIds);
    }
  }, [rooms]);

  useEffect(() => {
    // Give browser a moment to render the calendar
    setTimeout(() => {
      const monthViews = document.querySelectorAll('.rbc-month-view');
      monthViews.forEach(monthView => {
        monthView?.classList.add('rbc-month-view-short');
        if (calendarSpan === 'week' && (calendarGrouping === 'group' || calendarGrouping === 'favorites' || calendarGrouping === 'facilities')) {
          calculateCurrentWeekRow(currentDate);
        } else {
          monthView?.classList.remove('rbc-month-view-short');
          // Remove current week class from all rows
          const monthRows = document.querySelectorAll('.rbc-month-view .rbc-month-row');
          monthRows.forEach(row => {
            row.classList.remove('current-week');
          });
        }
      });
    }, 100);
  }, [calendarSpan, calendarGrouping, currentDate, groupUserIds]);


  useEffect(() => {
    if ((calendarGrouping === 'group' || calendarGrouping === 'favorites') && calendarSpan === "month") {
      setCalendarSDpan('week');
    }
  }, [calendarGrouping, calendarSpan]);

  useEffect(() => {
    if (userId && calendarGrouping !== "individual") {
      setSelectedUserId(userId);
    }
  }, [calendarGrouping, userId]);

  // Load favorite and default users from slice
  useEffect(() => {
    // Use the currently selected favorite list instead of just finding the first one
    const favoriteList = lists.find(list => 
      list.type === 'favorite' && 
      list.id === favoriteListValue?.value
    );
    const defaultList = lists.find(list => list.type === 'default');

    if (favoriteList) {
      setFavoriteUserIds(favoriteList.participants);
    }
    if (defaultList) {
      setDefaultParticipants(defaultList.participants);
    }
  }, [lists, favoriteListValue]);

  useEffect(() => {
    if (userSelectionType === 'default') {
      const savedUserIds = defaultParticipantUserIds;
      const savedUsers = users.filter(user => savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});
      const otherUsers = users.filter(user => !savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});

      setSelectedUsers(savedUsers);
      setAvailableUsers(otherUsers);
    }
    if (userSelectionType === 'favorite') {
      const savedUserIds = favoriteUserIds;
      const savedUsers = users.filter(user => savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});
      const otherUsers = users.filter(user => !savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});
      
      setSelectedUsers(savedUsers);
      setAvailableUsers(otherUsers);
    }
    if (userSelectionType === null) {
      setSelectedUsers([]);
      setAvailableUsers(users);
    }
  }, [userSelectionType, favoriteUserIds, defaultParticipantUserIds, users, userId]);

  useEffect(() => {
    if(sortedGroups && selectedGroupId === null) {
      // If no group is selected, set the first group as selected
      const firstGroup = sortedGroups[0];
      if (firstGroup && firstGroup.id) {
        setSelectedGroupId(firstGroup.id);
      }
    }
  }, [sortedGroups, selectedGroupId]);    

  useEffect(() => {
    if (calendarGrouping === 'favorites') {
      console.log("favoriteListValue:", favoriteListValue);
      const favoriteLists = lists.filter(list => list.type === 'favorite');

      // Early return if no favorite lists exist at all
      if (!favoriteLists || favoriteLists?.length === 0) {
        console.log("No favorite lists found, using empty array");
        setGroupUserIds([]);
        return;
      }

      // If user has favorite lists but none is selected, select the first one
      if (!favoriteListValue && favoriteLists.length > 0) {
        const firstFavoriteList = favoriteLists[0];
        setFavoriteListValue({
          value: firstFavoriteList.id,
          label: firstFavoriteList.name || 'お気に入りリスト'
        });
        return;
      }

      // Early return if favoriteListValue is null but favorite lists exist
      if (!favoriteListValue) {
        console.log("No favorite list selected, using empty array");
        setGroupUserIds([]);
        return;
      }
    
      const favoriteList = lists.find(list => 
        list.type === 'favorite' && 
        list.id === favoriteListValue.value
      );
      console.log("Filtered user IDs for faves:", favoriteList?.participants);
      setGroupUserIds(favoriteList ? favoriteList.participants : []);
    }
  }, [calendarGrouping, favoriteListValue?.value, lists.length]); 

  useEffect(() => {
    if (calendarGrouping === 'group') {
      if(selectedGroupId) {
        const filteredUserIds: number[] = Array.from(new Set(
          preloadData.groupsUsers
            .filter((gu) => gu.group_id === selectedGroupId)
            .filter((gu) => gu.user_id !== userId) // Exclude the current user
            .map((gu) => gu.user_id)
        ));
        console.log("Filtered user IDs for group:", filteredUserIds);
        setGroupUserIds(filteredUserIds);
      } else {
        // Prevent unintentional clearing on partial state - This used to cause the calendar to clear if navigation was done too soon
        // setGroupUserIds([]);
        return;
      }
    }
  }, [calendarGrouping, favoriteListValue, selectedGroupId]); 

  const calculateCurrentWeekRow = (weekDate: Date = currentDate) => {
    const needleDate = weekDate.getDate();
    const monthViews = document.querySelectorAll('.rbc-month-view');

    monthViews.forEach(monthView => {
      const monthRows = monthView.querySelectorAll('.rbc-month-view .rbc-month-row');
    
      monthRows.forEach((row, index) => {
        const disqualified = (needleDate > 20 && index === 0) || (needleDate < 10 && index > 3);
        // Find date cells in this row
        const dateCells = row.querySelectorAll('.rbc-date-cell');
        
        // Check if any date in this row is from the current week
        const isCurrentWeek = Array.from(dateCells).some(cell => {
          const cellText = cell.textContent || '';
          const cellDate = parseInt(cellText, 10);
          
          if (isNaN(cellDate)) return false;
          
          // Create a date for this cell
          const cellDateObj = new Date(currentDate);
          cellDateObj.setDate(cellDate);
          
          // Check if this date is in the same week as today
          const todayWeekStartDate = moment(weekDate).startOf('week').toDate();
          const todayWeekEndDate = moment(weekDate).endOf('week').toDate();
          
          return cellDateObj >= todayWeekStartDate && cellDateObj <= todayWeekEndDate;
        });
        
        // Add/remove class based on whether this is the current week
        if (isCurrentWeek && !disqualified) {
          row.classList.add('current-week');
        } else {
          row.classList.remove('current-week');
        }
      });
    });
  };

  // Handle favorite users button click
  const handleFavoriteUsersClick = () => {
    setUserSelectionType('favorite');
    //initializeUserSelection('favorite');
    const favoriteList = lists.find(list => list.type === 'favorite' && list.creator_id === userId && list.id === favoriteListValue?.value);
    const favoriteUserIds = favoriteList ? favoriteList.participants : [];
    setSelectedUsers(favoriteUserIds.map(id => {
      const user = users.find(user => user.id === id);
      return { user: user || null, required: false };
    }));
  };

  // Handle default participants button click
  const handleDefaultParticipantsClick = () => {
    setUserSelectionType('default');
    initializeUserSelection('default');
  };

  // Initialize user selection modal based on type
  const initializeUserSelection = (type: 'favorite' | 'default') => {
    // Get all users from preload data
    const allUsers = preloadData.users || [];
    
    // Get the saved user IDs based on type
    const savedUserIds = type === 'favorite' ? favoriteUserIds : defaultParticipantUserIds;
    
    // Find the user objects for the saved IDs
    const savedUsers:ReservationsParticipant[] = allUsers.filter(user => savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});
    
    // Set available users (those not in the saved list)
    const otherUsers:ReservationsParticipant[] = allUsers.filter(user => !savedUserIds.includes(user.id)).map(user => {return {user: user, required: false};});
    
    // Set the state variables
    setSelectedUsers(savedUsers);
    setAvailableUsers(otherUsers);
  };

  // Save selected users and close modal
  const handleSaveUserSelection = async () => {
    const selectedUserIds = selectedUsers.map(user => user.user?.id || 0) || [];

    if (userSelectionType === 'favorite') {
      if (!favoriteListValue) {
        // You must create and select a favorite list first
        alert("「お気に入りリスト」を作成して選択してください。");
        return;
      }
      await dispatch(updateList({
        id: favoriteListValue?.value || 0,
        type: 'favorite',
        participants: selectedUserIds,
        name: favoriteListValue?.label || 'お気に入りリスト'
      }));
      setFavoriteUserIds(selectedUserIds);
    } else if (userSelectionType === 'default') {
      const defaultList = lists.find(list => list.type === 'default' && list.creator_id === userId);
      await dispatch(createList({
        type: 'default',
        participants: selectedUserIds
      }));
      setDefaultParticipants(selectedUserIds);
    }
    
    // Close the modal
    setUserSelectionType(null);
  };

  // Close modal without saving
  const handleCloseUserSelection = () => {
    setUserSelectionType(null);
  };

  // Handle month change to load new reservations
  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
  };

  // Create events from the reservations
  const events = createScheduleEvents(reservations.data, currentDate, types.data);

 const goToBack = () => {
  let newDate = new Date(currentDate);
  if (calendarSpan === 'month') {
       // for month always set to the first day of the target month
    // to avoid issues with different month lengths
    const targetMonth = newDate.getMonth() - 1;
    const targetYear = newDate.getFullYear();
    if (targetMonth < 0) {
      newDate = new Date(targetYear - 1, 11, 1); // december of previous year
    } else {
      newDate = new Date(targetYear, targetMonth, 1); // Ffirst day of target month
    }
  }

 else if (calendarSpan === 'week') { 
    newDate.setDate(newDate.getDate() - 7); 
    calculateCurrentWeekRow(newDate);
  }
  else if (calendarSpan === 'day') { 
    newDate.setDate(newDate.getDate() - 1); 
  }
  
  setCurrentDate(newDate);
};

const goToNext = () => {
  let newDate = new Date(currentDate);
  
  if (calendarSpan === 'month') { 

    const targetMonth = newDate.getMonth() + 1;
    const targetYear = newDate.getFullYear();
    
    // Handle year transition
    if (targetMonth > 11) {
      newDate = new Date(targetYear + 1, 0, 1); // janof next year
    } else {
      newDate = new Date(targetYear, targetMonth, 1); // first day of target month
    }
  }
  else if (calendarSpan === 'week') { 
    newDate.setDate(newDate.getDate() + 7); 
    calculateCurrentWeekRow(newDate);
  }
  else if (calendarSpan === 'day') { 
    newDate.setDate(newDate.getDate() + 1); 
  }
  
  setCurrentDate(newDate);
};

  const goToCurrent = () => {
    const now = new Date();
    setCurrentDate(now);
  };

  // logic to display the date based on daily, weekly and monthly views
  let currentDateText = "";

if (calendarSpan === "month") {
  currentDateText = moment(currentDate).format("YYYY年 M月");
} else if (calendarSpan === "week") {
  const weekStart = moment(currentDate).startOf('week');  // sunday
  const weekEnd = moment(currentDate).endOf('week');   // saturday


  if (weekStart.year() === weekEnd.year() && weekStart.month() === weekEnd.month()) {
    currentDateText = `${weekStart.format("YYYY年 M月 D日")}〜${weekEnd.format("D日")}`;
  }

  else if (weekStart.year() === weekEnd.year()) {
    currentDateText = `${weekStart.format("YYYY年 M月 D日")}〜${weekEnd.format("M月 D日")}`;
  }

  else {
    currentDateText = `${weekStart.format("YYYY/M/D")}〜${weekEnd.format("YYYY/M/D")}`;
  }
} else {
  currentDateText = moment(currentDate).format("YYYY年 M月 D日");
}

  const todayLabel = calendarSpan === "day"
  ? "今日"
  : calendarSpan === "week"
    ? "今週"
    : calendarSpan === "month"
      ? "今月"
      : "今日";

  const dateControlBar = (
    <div style={{
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      marginTop:"0.5rem",

    }}>
      <span style={{
        fontSize: isMobile ? "16px" : "20px", 
        display: "flex", 
        alignItems: "center", 
        fontWeight: "bold",
        }}>
        {currentDateText}
      </span>
      <div style={{ display: "flex", alignItems: "center" }}>
        {(facilitiesDisplayed && (calendarRoomIds.length === 0 || sortedGroups.length === 0)) ?
           <div>読み込み中...</div>
         : null}
        <div className="btn-back">
          <AppIconButton onClick={goToBack} icon={<ArrowLeftIcon />} />
        </div>
          <label
            className="btn-current"
            onClick={goToCurrent}
            style={{
              cursor: "pointer",
              fontSize: "11px",
              fontWeight: "bold",
              margin:"0 1rem",
              backgroundColor: "#666",
              borderRadius: "6px",
              color: "white",
              padding: "3px 4px"
              }}>
              {todayLabel}
          </label>
        <div className="btn-next">
          <AppIconButton onClick={goToNext} icon={<ArrowRightIcon />} />
        </div>
      </div>
      {/*{reservationsLoading && <span style={{ display: "flex", alignItems: "center", fontSize: "2rem", color: "#666" }}>(読み込み中...)</span>}*/}
    </div>
  );

  // Navigate to new reservation page with current date
  const handleNewReservation = () => {
    history.push(appRouteEnums.PATH_NEW_RESERVATION);
  };

  const reservationCalendarInputs = calendarUserIds.map((friendId) => {
    const friendEvents = (calendarGrouping === "individual") ? events : events.filter((event) => {
      const reservation = event.reservation;
      return reservation?.participants.some((participant) => participant.user.id === friendId) || reservation?.creator_id === friendId;
    });
    const friendUser = preloadData.users.find((user) => user.id === friendId);
    const friendName = friendUser ? `${friendUser.last_name} ${friendUser.first_name}` : "Unknown User";
    const isUserCalendar = (friendId === userId);
    const displayName = calendarGrouping === "individual" ? false : calendarSpan === "week" ? true : isUserCalendar ? true : true;

    return {
      key: friendId, // user id, e.g. 7
      name: friendName,
      events: friendEvents,
      isUserCalendar: isUserCalendar,
      displayName: displayName,
    };
  });

  const flatUserResources = reservationCalendarInputs.map(input => ({
    id: input.key,
    title: input.name,
  }));

  const flatUserInputs = reservationCalendarInputs
      .filter(input => input.events && Array.isArray(input.events))
      .reduce((acc, input) => {
        const key = input.key || 0;
        
        acc.push(...input.events.map(event => ({
          ...event,
          userKey: key,
          userName: input.name,
          resourceId: key,
          start: event.startDate,
          end: event.endDate,
          creatorId: event.reservation?.creator_id,
        })));
      
      return acc;
    }, [] as Array<any>);

//  console.log("flatEvents", flatInputs);

  const facilityCalendarInputs = calendarRoomIds.map((roomId) => {
    const roomEvents = events.filter((event) => {
      const reservation = event.reservation;
      return reservation?.room_ids.some((room) => room === roomId);
    });
    const room = rooms.data.find((room) => room.id === roomId);
    const roomName = room ? room.name : "Unknown Room";

    return {
      key: roomId,
      name: roomName,
      events: roomEvents,
      isUserCalendar: false, // data broken into rooms, not by user, so it's never the user's calendar
      displayName: true,     // always show the name of the room, there's no view we don't want to show it
    };
  });

  const flatFacilityResources = facilityCalendarInputs.map(input => ({
    id: input.key,
    title: input.name,
  }));

  const flatFacilityInputs = facilityCalendarInputs
    .filter(input => input.events && Array.isArray(input.events))
    .reduce((acc, input) => {
      const key = input.key || 0;
      
      acc.push(...input.events.map(event => ({
        ...event,
        facilityKey: key,
        facilityName: input.name,
        resourceId: key,
        start: event.startDate,
        end: event.endDate,
      })));
    
    return acc;
  }, [] as Array<any>);

  const dailyFacilityGrouping = (calendarSpan === "day" && calendarGrouping === "facilities");
  const dailyUserGouping = (calendarSpan === "day" && calendarGrouping !== "individual" && calendarGrouping !== "facilities");
  const dailyGrouping = dailyFacilityGrouping || dailyUserGouping;

  const calendarInputs =
    dailyFacilityGrouping ? [flatFacilityInputs] :
    facilitiesDisplayed ? facilityCalendarInputs :
    dailyUserGouping ? [flatUserInputs] :
    reservationCalendarInputs;

//     useEffect(() => {
//   console.log("DEBUG: calendarInputs", calendarInputs);
//   console.log("DEBUG: facilitiesDisplayed", facilitiesDisplayed);
//   console.log("DEBUG: holidays", holidays);
// }, [calendarInputs, facilitiesDisplayed, holidays]);

  const calenderResources = dailyFacilityGrouping ? flatFacilityResources :
    dailyUserGouping ? flatUserResources :
    undefined;

//     const loadingOverlay = showLoadingOverlay && (
//   <div style={{
//     position: 'fixed',
//     top: 0,
//     left: 0,
//     right: 0,
//     bottom: 0,
//     //backgroundColor: 'rgba(0, 0, 0, 0.1)',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     zIndex: 9999,
//   }}>
//     <div style={{
//       backgroundColor: 'white',
//       padding: '2rem',
//       borderRadius: '8px',
//       boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
//     }}>
//       <span style={{ 
//         fontSize: '20px', 
//         color: '#666',
//         fontWeight: 'normal'
//       }}>
//         読み込み中...
//       </span>
//     </div>
//   </div>
// );

  const calenderElements = calendarInputs.map((input, idx) => {
    if (calendarGrouping === "individual" && idx > 0) return null;
    return(
      <ScheduleCalendar
        key={`${input.key}-${calendarSpan}-${calendarGrouping}`}
        calenderUserId={(calendarGrouping === "individual" ? selectedUserId : input.key) || 0}
        facilityId={facilitiesDisplayed ? input.key : undefined}
        instanceKey={`${input.key}-${calendarSpan}-${calendarGrouping}`}
        calendarIndex={idx + 1}
        currentDate={currentDate}
        resources={calenderResources}
        events={dailyGrouping ? input : input.events}
        span={calendarSpan}
        grouping={calendarGrouping}
        holidays={holidays ?? []}
        defaultParticipants={defaultParticipantUserIds}
        ownCalendar={dailyGrouping ? true : userId === input.key}
        name={input.name}
        onNavigateRequest={handleNavigate}
        dailyGouping={dailyGrouping}
        refreshReservations={refreshReservations}
      />
    );
  });

  const allCalendarElements = (
    
    <div id="allCalendarElements" style={{
      display: "flex",
      flexDirection: stackedView ? "column" : columnsView ? "row" : undefined,
      width: "100%",
      height: (calendarSpan === "week" && calendarGrouping === "individual") ? "100%" : "100%",
      overflow:"auto", // IMPORTANT: this setting enabled scrollbars around the calendar
      border:"2px solid white",
      borderRadius:"10px",
      marginTop:"0.5rem",
      overscrollBehavior: "none",
      position:"relative"
      }}>
  
        <div id="headerDummy"
          ref={headerDummyRef}
        style={{
            position:"fixed",
            zIndex:"30",
            flexShrink: 0,
            width: isMobile ? '100px' : '150px',
            minWidth: isMobile ? '100px' : '150px',
            height: '25px',
            display: "none",
            background:"#e4e4e4",
            borderRight:"1px solid #bbb"
        }}/>

        <div id="headerDummyIndividualWeekly"
          ref={headerDummyIndividualWeeklyRef}
        style={{
            position:"fixed",
            zIndex:"30",
            flexShrink: 0,
            width: "46x",
            minWidth: "46px",
            height: '0px',
            display: "none",
            background:"#e4e4e4",
            borderRight:"1px solid #bbb"
        }}/>
      
      {calenderElements}
  
    </div>);

  const buttonsContent = [
    {text: "デフォルト予定参加者登録", function: handleDefaultParticipantsClick, hiddenOnFacilities: true},
    {text: "お気に入りメンバー登録", function: handleFavoriteUsersClick, hiddenOnFacilities: true},
    //{text: "新しい予約", function: handleNewReservation, hiddenOnFacilities: false},
  ];

  const headerButtons = buttonsContent.map((button, index) => (
    <div onClick={button.function}
    >
      <Button
        key={index}
        variant="contained"        
        sx={{
          fontSize: '11px',
          fontWeight: 'bold',
          paddingTop: '1rem',
          bgcolor: '#ff6a0c',
          '&:hover': { bgcolor: '#e05a00', boxShadow: 'none' },
          height: isMobile ? "30px" : "40px",
          borderRadius: '50px',
          display: (button.hiddenOnFacilities && facilitiesDisplayed) ? "none" : undefined,
          boxShadow: 'none',
        }}
      >
        {button.text}
      </Button>
    </div>
  ));

  const groupingButtons = reservationsDisplayed ? (
    <CalendarLengthButtonGroup>
      <CalendarLengthButton
        className="calendar-length-button grouping-individual"
        selected={calendarGrouping === "individual"}
        onClick={() => setCalendarGrouping("individual")}
      >
        個人
      </CalendarLengthButton>
      <CalendarLengthButton
        className="calendar-length-button grouping-group"
        selected={calendarGrouping === "group"}
        onClick={() => setCalendarGrouping("group")}
      >
        グループ
      </CalendarLengthButton>
      <CalendarLengthButton
        className="calendar-length-button grouping-favorites"
        selected={calendarGrouping === "favorites"}
        onClick={() => setCalendarGrouping("favorites")}
      >
        お気に入りメンバー一覧
      </CalendarLengthButton>
    </CalendarLengthButtonGroup>
  ) : null;

  //ユーザーとグループのリアルタイム履歴反映のリスト読み込み
    useEffect(() => {
      if (!list || !groupList || !userId || list.length === 0) return;

      if (list && groupList) {
        
        const userOption = list.map((user) => ({
          label: `${user.last_name} ${user.first_name}`,
          value: user.id.toString(),
          type: user.type,
        }));
        
        const currentUserOption = userOption.find((user) => user.value === userId?.toString());
  
        const userOptionWithoutCurrentUser = userOption.filter((user) => user.value !== userId?.toString());
        
        const groupOption = groupList.map((group) => ({
          label: group.name,
          value: group.id.toString(),
          type: group.type,
        }));
  
        setUserOptions(
          [currentUserOption, ...userOptionWithoutCurrentUser].filter(
            (option): option is { label: string; value: string; type: string } => option !== undefined
          )
        );

        setGroupOptions(groupOption);
  
        // 初回アクセス時のみログインユーザー既存選択固定
        // if (!hasAutoSelected && currentUserOption) {
        //   setSelectedUserId(currentUserOption.value);
        //   setHasAutoSelected(true);
        // }
  
      }
    }, [list, groupList]);


  const individualSelectionDropdown = (reservationsDisplayed && calendarGrouping === "individual") ? (
    <Grid container item xs={12} md={12} lg={4} xl={3} style={{marginTop:"1rem"}}>
      <Grid item xs={12}>

        <AppCustomeSelect
          style={{ fontSize: isMobile ? "11px" : "13px" }}
          value={
            userOptions
              ? userOptions.find((opt) => opt.value === String(selectedUserId)) || null
              : null
          }
          // value={
          //   sortedUsers
          //     .map((user) => ({ value: user.id, label: `${user.last_name} ${user.first_name}` }))
          //     .find((opt) => opt.value === selectedUserId) || null
          // }
          // options={sortedUsers.map((user) => ({
          //   value: user.id,
          //   label: `${user.last_name} ${user.first_name}`,
          // }))}
          options={userOptions ?? []}
          onChange={(option) => {
            setSelectedUserId(option ? Number(option.value) : null);
            // weeklyScheduleApi.updateOrderListName(Number(option.value));         
            storeSelection({ user_id: userId, selected_user_id: option.value });

            //セッション保存なしになったためコメントアウト
            // Update sessionStorage with new user order
            // if (option?.value) {
            //   const userId = Number(option.value);
            //   if (userId !== null && userId !== undefined && !Number.isNaN(userId)) {
            //     const stored = sessionStorage.getItem("userListOrder");
            //     let list: number[] = [];

            //     try {
            //       if (stored) {
            //         list = JSON.parse(stored);
            //         if (!Array.isArray(list)) list = [];
            //       }
            //     } catch {
            //       list = [];
            //     }

            //     // Remove userId if exists, then add to front
            //     list = list.filter((id) => id !== userId);
            //     list.unshift(userId);

            //     sessionStorage.setItem("userListOrder", JSON.stringify(list));
            //   }
            // }
          }}
          placeholder="個人を選択してください"
          isSearchable={true}
          isClearable={false}
          name="user-selector"
          aria-label="ユーザー選択"
          playwrightName="user-selector"
        />
      </Grid>
    </Grid>
  ) : null;

  const groupSelectionDropdown = (reservationsDisplayed && calendarGrouping === "group") ? (
    <Grid container item xs={12} md={12} lg={4} xl={3} style={{marginTop:"1rem"}}>
      <Grid item xs={12}>
        <AppCustomeSelect
          style={{ fontSize: isMobile ? "11px" : "13px" }}
          // value={
          //   sortedGroups
          //     .map((group) => ({ value: group.id, label: group.name }))
          //     .find((opt) => opt.value === selectedGroupId) || null
          // }
          value={
            groupOptions
              .find((opt) => opt.value === String(selectedGroupId)) || null
          }
          // options={sortedGroups.map((group) => ({
          //   value: group.id,
          //   label: group.name,
          //   label: `${group.name} (${group.id})`,
          // }))}
          options={groupOptions ?? []}
          onChange={(option) => {
            setSelectedGroupId(option ? Number(option.value) : null);
            // weeklyScheduleApi.updateGroupOrder(Number(option.value));
            
            //選択したグループの履歴保存
            storeGroupSelection({ user_id: userId, selected_group_id: option.value });

            //セッション保存なしになったためコメントアウト
            // Update sessionStorage with new user order
            // if (option?.value) {
            //   const userId = Number(option.value);
            //   if (userId !== null && userId !== undefined && !Number.isNaN(userId)) {
            //     const stored = sessionStorage.getItem("groupListOrder");
            //     let list: number[] = [];

            //     try {
            //       if (stored) {
            //         list = JSON.parse(stored);
            //         if (!Array.isArray(list)) list = [];
            //       }
            //     } catch {
            //       list = [];
            //     }

            //     // Remove userId if exists, then add to front
            //     list = list.filter((id) => id !== userId);
            //     list.unshift(userId);

            //     sessionStorage.setItem("groupListOrder", JSON.stringify(list));
            //   }
            // }

          }}
          placeholder="グループを選択してください"
          isSearchable={false}
          isClearable={false}
        />
      </Grid>
    </Grid>
  ) : null;

  const favoriteSelectionDropdown = (reservationsDisplayed && calendarGrouping === "favorites") ? (
    <Grid container xs={12} md={12} lg={4} xl={3} style={{marginTop:"1rem"}}>
      <Grid item xs={12}>
        <AppCustomeSelect
          value={favoriteListValue}
          options={lists
            .filter((list) => list.type === 'favorite')
            .map((list) => ({
            value: list.id,
            label: list.name,
          }))}
          onChange={(option) => {
            console.log("Selected option:", option);
            setFavoriteListValue(option ? option : null);
          }}
          placeholder="リストを選択してください"
          isSearchable={false}
          isClearable={false}
          className="user-selection-dropdown"
        />
      </Grid>
    </Grid>
  ) : null;

  const showMonthButton = (calendarGrouping === "individual" && reservationsDisplayed) ? true : false; 

  const hideSchedule = false;

  // detect browser

  // const browser = useBrowser();

  // let paddingBottom;

  // switch (browser) {
  //   case "iOS Chrome":
  //     paddingBottom = isMobile ? "0rem" : "1.5rem";
  //     break;
  //   case "iOS Safari":
  //     paddingBottom = isMobile ? "7.5rem" : "1.5rem";
  //     break;
  //   default:
  //     paddingBottom = isMobile ? "5rem" : "1.5rem";
  // }

  const deviceInfo = useDeviceDetection();
  const paddingBottom = useDynamicPadding();

  

  if (!userId || !preloadData.users || preloadData.users.length === 0) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
        background: "#f5f5f5"
      }}>
        <Box position="relative" display="inline-flex">
          {/* bottom one is the full circle */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={60}
            thickness={6}
            sx={{ color: "#ff6a0c" }}
          />
          {/* rotating arc */}
          <CircularProgress
            variant="indeterminate"
            size={60}
            thickness={6}
            sx={{
              color: "#0873fc",
              position: "absolute",
              left: 0,
              top: 0,
            }}
          />
        </Box>
        <span style={{ marginTop: "1rem", color: "#888", fontSize: "20px" }}>スケジュール読み込み中...</span>
      </div>
    );
  }

  return ( (hideSchedule) ? <span style={{lineHeight: "250px"}}>Under construction</span> :
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "#f5f5f5",
        overflow: "hidden",
        paddingTop: isMobile ? "7rem" : "8rem",
        paddingBottom: paddingBottom,
        paddingLeft: isMobile ? "0.5rem" : "1.5rem",
        paddingRight: isMobile ? "0.5rem" : "1.5rem",
        /*paddingBottom: "env(safe-area-inset-bottom, 0px)",*/
      }}
    >
    
      <div style={{display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start" , width: "100%"}}>
        <ScheduleHeaderContainer>
          <ScheduleTitle>{facilitiesDisplayed ? "施設予約" : "スケジュール"}</ScheduleTitle>
          <ScheduleHeaderButtons>
            {headerButtons}
          </ScheduleHeaderButtons>
        </ScheduleHeaderContainer>
        <StyledDivider />
        {groupingButtons}
        {individualSelectionDropdown}
        {groupSelectionDropdown}
        {favoriteSelectionDropdown}
        {isMobile ? <div style={{ height: "1rem" }} /> : <StyledDivider />}
        <CalendarLengthButtonGroup>
        <CalendarLengthButton
          className="calendar-length-button"
          style={{ display: showMonthButton ? undefined : "none" }}
          selected={calendarSpan === "month"}
          onClick={() => setCalendarSDpan("month")}
          data-playwright="schedule-month-button"
        >
          月
        </CalendarLengthButton>
        <CalendarLengthButton
          className="calendar-length-button"
          selected={calendarSpan === "week"}
          onClick={() => setCalendarSDpan("week")}
          data-playwright="schedule-week-button"
        >
          週
        </CalendarLengthButton>
        <CalendarLengthButton
          className="calendar-length-button"
          selected={calendarSpan === "day"}
          onClick={() => setCalendarSDpan("day")}
          data-playwright="schedule-day-button"
        >
          日
        </CalendarLengthButton>
        </CalendarLengthButtonGroup>

        {/*<span style={{display: "none"}}>Grouping: {calendarGrouping}, Length: {calendarSpan}, Stacked: {stackedView ? "true" : "false"}, Columns: {columnsView ? "true" : "false"}, isMobile: {isMobile ? "true" : "false"}</span>*/}
        {dateControlBar}
      </div>
      
      {/*<div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", height: "100%" }}>*/}
      {allCalendarElements}
      {/* User Selection Modal */}
      <Dialog
        open={userSelectionType !== null}
        onClose={handleCloseUserSelection}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
        sx: {
          ...(!isMobile && { borderRadius: '10px', width: "600px" }),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          },
        }}
      >
        <DialogContent
          sx={{
            padding: '0',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            overflow: 'hidden',
            '& > .MuiBox-root': {
              padding: '0',
              paddingTop:"3rem"
              },
          }}
        >
          <Box sx={{
        flexGrow: 1, // This Box will take up available space before the action buttons
        overflowY: 'auto', // Make this specific Box scrollable if its content overflows
        p: { xs: 2, sm: 3 }, // Consistent padding around the main content
      }}>
        
            <FormTitleHeader>
              {userSelectionType === 'favorite' ? 'お気に入りメンバー登録' : 'デフォルト予定参加者登録'}
              <IconButton
                  aria-label="close"
                  onClick={handleCloseUserSelection}
                  sx={{ color: (theme) => theme.palette.grey[700],
                  fontSize:'24px',
                  padding: 0,
                  paddingLeft:'4px',
                  paddingRight:'4px',
                  '&:hover': {
                  backgroundColor: 'transparent',
              }}}>
                  ×
              </IconButton>
            </FormTitleHeader>
            <div style={{display: userSelectionType === 'favorite' ? "flex" : "none", flexDirection:"column", gap:"1rem", padding:"1rem", marginTop:"1rem"}}>
              <AppCustomeSelect
              style={{
                  fontSize:"13px"
                }}
                value={favoriteListValue}
                options={lists
                  .filter((list) => list.type === 'favorite')
                  .map((list) => ({
                  value: list.id,
                  label: list.name,
                }))}
                onChange={(option) => {
                  console.log("Selected option:", option);
                  setFavoriteListValue(option ? option : null);
                  setSelectedUsers(
                    option ? lists.find(list => list.id === option.value)?.participants.map(userId => ({
                      user: users.find(user => user.id === userId) || null,
                      required: false
                    })) || [] : []
                  );
                }}
                placeholder="リストを選択してください"
                isSearchable={false}
                isClearable={false}
                className="user-selection-dropdown"
              />
              <Box sx={{
                display: 'flex',
                gap: '5px',
                padding: '0',
                width:"100%",
                '& > *': { // Target direct children (PassChangeButton components)
                  flex: 1, // Make buttons share the available width
                  padding:0
                },
                }}>
              
                <PassChangeButton
                  onClick={async () => {
                    // can't make more than 10 favorite lists
                    if (lists.filter(list => list.type === "favorite").length >= 10) {
                      alert("お気に入りリストは10個まで作成できます。");
                      return;
                    }

                    const newName = prompt("新しいリストの名前を入力してください:");
                    if (newName) {
                      try {
                        const resultAction = await dispatch(createList({
                          type: 'favorite',
                          participants: [],
                          name: newName
                        }));
                        console.log("List created:", resultAction);
                        setFavoriteListValue({ value: resultAction.payload.data.id, label: newName });
                        setSelectedUsers([]);
                        setAvailableUsers(users.map(user => ({ user, required: false })));
                      } catch (error) {
                        console.error("Error creating list:", error);
                        alert("リストの作成に失敗しました。");
                      }
                    }
                  }}
                >
                  リストを作成する
                </PassChangeButton>
    

          
                <PassChangeButton
                  onClick={async () => {
                    if (!favoriteListValue) {
                      alert("リストを選択してください");
                      return;
                    }
                    const newName = prompt("新しいリストの名前を入力してください:", favoriteListValue.label);
                    if (newName) {
                      await dispatch(updateList({
                        id: favoriteListValue.value,
                        type: 'favorite',
                        name: newName
                      }));
                      setFavoriteListValue({ value: favoriteListValue.value, label: newName });
                    }
                  }
                  }
                  disabled={!favoriteListValue}
                >
                  リストの名前変更
                </PassChangeButton>
      

                <PassChangeButton
                  onClick={async () => {
                    if (lists.filter(list => list.type === "favorite").length === 1) {
                      alert("リストを削除できません。リストが1つしかありません。");
                      return;
                    }
                    const confirmDelete = window.confirm("リストを削除しますか？この操作は取り消せません。");
                    if (!confirmDelete) return;

                    if (favoriteListValue) {
                      await dispatch(deleteList(favoriteListValue.value));
                      const newFavoriteSelection = lists.find(list => list.type === "favorite" && list.creator_id === userId);
                      setFavoriteListValue(newFavoriteSelection ? { value: newFavoriteSelection.id, label: newFavoriteSelection.name || 'お気に入りリスト' } : null);
                    } else {
                      alert("リストを選択してください");
                    }
                  }}
                  danger={true}
                >
                  リストを削除
                </PassChangeButton>
            
              </Box>
            </div>
            <div
            style={{
                padding: isMobile ? "1rem" : "2rem"
              }}
              >
            <ParticipantsSelector
              currentUserId={userId || 0}
              users={users}
              groups={sortedGroups}
              groupsUsers={groupsUsers}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              canSelectSelf={userSelectionType === 'default'}
              userSelectionType={userSelectionType}
              leftListHeight={isMobile ? "450px" : "500px"} 
              rightListHeight={isMobile ? "352px" : "402px"} 
            />   
            </div>
            {/* Action Buttons */}
            <Box sx={{
        display: 'flex',
        flexShrink: 0, // Prevent this Box from shrinking
        p: { xs: 1.5, sm: 2 }, // Padding for the actions area
        gap: "1rem", // Space between buttons
        borderTop: (theme) => `1px solid ${theme.palette.divider}`, // Visual separation
        backgroundColor: (theme) => theme.palette.background.paper, // Ensure background consistency
        '& > *': { // Target direct children (PassChangeButton components)
          flex: 1, // Make buttons share the available width
        },
      }}>
              <PassChangeButton
                onClick={handleCloseUserSelection}
                danger={true}
              >
                キャンセル
              </PassChangeButton>
              <PassChangeButton
                onClick={handleSaveUserSelection}
              >
                保存
              </PassChangeButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// StyledDivider copied from ReservationForm.tsx
const StyledDivider = scStyled.div`
  border-top:1px solid ${(p) => p.theme.colors.lightgray09};
  width: 100%; 
  margin:1rem 0;
  padding:0;
`;

const FormTitleHeader = scStyled.div`
  background-color: ${(p) => p.theme.colors.lightgray09};
  padding:0.5rem;
  padding-left:1rem;
  font-size:14px;
  font-weight:bold;
  text-align:left;
  display: flex;    
  justify-content: space-between; 
  align-items: center;
  position: absolute;
    top: 0;
    z-index: 10;
    border-bottom:1px solid #bbb;
    width:100%;
    padding-right:1rem;
`;
